<template>
	<v-row dense data-tour="kpis">
		<v-col v-for="t in tiles" :key="t.key" cols="6" sm="4" lg="3">
			<v-card outlined class="kpi-tile pa-3" height="100%">
				<div class="d-flex align-center">
					<span class="text-caption grey--text text--darken-1 text-uppercase kpi-label">{{ t.label }}</span>
					<v-tooltip bottom max-width="300">
						<template v-slot:activator="{ on, attrs }">
							<v-icon x-small class="ml-1 kpi-help" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
						</template>
						<span>{{ t.help }}</span>
					</v-tooltip>
				</div>
				<div class="kpi-value">{{ t.value }}</div>
				<div class="d-flex align-center" style="gap: 6px">
					<v-chip x-small label :color="t.color" text-color="white">{{ t.delta }}</v-chip>
					<span class="text-caption grey--text text-truncate">prima {{ t.before }}</span>
				</div>
				<v-sparkline v-if="t.spark && t.spark.length > 1" :value="t.spark" :color="t.color === 'error' ? '#E53935' : '#1976D2'" line-width="3" smooth padding="4" height="36" class="mt-1" auto-draw />
			</v-card>
		</v-col>
	</v-row>
</template>

<script>
import { formatNumber, formatPct, formatDelta, deltaColor, formatDurata, formatDecimale } from './format';

// Riquadri principali delle Statistiche: nomi comprensibili e spiegazione al passaggio del mouse
export default {
	name: 'AnalyticsKpis',
	props: {
		kpis: { type: Object, required: true },
		previous: { type: Object, required: true },
		deltas: { type: Object, required: true },
		trend: { type: Array, default: () => [] },
		// statistiche del singolo prodotto: riquadri da non mostrare e spiegazioni diverse da quelle del sito
		hidden: { type: Array, default: () => [] },
		helps: { type: Object, default: () => ({}) },
	},
	computed: {
		tiles() {
			const k = this.kpis;
			const p = this.previous;
			const d = this.deltas;
			const visite = this.trend.map(t => t.visits);
			const visitatori = this.trend.map(t => t.visitors);
			const tile = (key, label, fmt, help, spark = null) => ({
				key,
				label,
				help: this.helps[key] || help,
				spark,
				value: fmt(k[key]),
				before: fmt(p[key]),
				delta: formatDelta(d[key]),
				color: deltaColor(d[key]),
			});
			return [
				tile('visitors', 'Visitatori', formatNumber, 'Persone diverse che hanno visitato il sito nel periodo (chi torna più volte conta una volta sola).', visitatori),
				tile('visits', 'Visite', formatNumber, 'Numero di visite al sito. Una persona che entra tre volte in giorni diversi fa tre visite.', visite),
				tile('newVisitors', 'Nuovi visitatori', formatNumber, 'Persone arrivate sul sito per la prima volta nel periodo.'),
				tile('pageViews', 'Pagine viste', formatNumber, 'Totale delle pagine aperte da tutti i visitatori.'),
				tile('pagesPerVisit', 'Pagine per visita', v => formatDecimale(v, 1), 'Quante pagine guarda in media un visitatore in ogni visita: più è alto, più il sito incuriosisce.'),
				tile('avgDuration', 'Durata media visita', formatDurata, 'Quanto tempo resta in media un visitatore sul sito in ogni visita.'),
				tile(
					'engagementRate',
					'Visite coinvolte',
					formatPct,
					"Percentuale di visite in cui il visitatore ha davvero usato il sito: è rimasto almeno 10 secondi, ha aperto più di una pagina o ha compiuto un'azione. Il resto sono visite chiuse subito.",
				),
				tile(
					'conversionRate',
					'Tasso di conversione',
					formatPct,
					`Ordini arrivati dal sito diviso visite. Nel periodo: ${formatNumber(k.orders)} ordini su ${formatNumber(k.visits)} visite (prima ${formatNumber(
						p.orders,
					)}). Include anche gli ordini "sito" inseriti a mano dal gestionale.`,
				),
			].filter(t => !this.hidden.includes(t.key));
		},
	},
};
</script>

<style scoped>
.kpi-value {
	font-size: 1.6rem;
	font-weight: 600;
	line-height: 1.3;
	margin: 2px 0 4px;
	white-space: nowrap;
}
.kpi-label {
	letter-spacing: 0.03em;
}
.kpi-help {
	cursor: help;
	opacity: 0.6;
}
</style>
